import React, { useState } from 'react';
import StaticLayout from '@components/layout/StaticLayout';
import ListingFilterWidget from '@components/widgets/listing-filter-widget/ListingFilterWidget';
import { IListingFilterWidgetSection } from '@components/widgets/listing-filter-widget';
import { toast } from 'react-toastify';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { DEFAULT_ERROR_TEXT } from '@utils';
import ListingWidget from '@components/widgets/listing-widget/ListingWidget';
import { getFilter, setFilter } from '@utils/filter-utils';
import { IFilterModel } from '@components/widgets/listing-widget';
import { IListingData } from '@models/listing';
import { filterSectionsContent, headers } from './content';
import { IAbstractRecord } from '@models';
import { useLazyQuery } from '@apollo/client';
import { getTreaties } from './queries';
import { mapToListingData, toLookupsData } from './utils';
import { IEnhancedMenuItem } from '@components/table/types';
import { isEmpty } from 'lodash';
import NewTreatyDrawer from '../new-treaty-drawer/NewTreatyDrawer';
import { valueOrNull } from '@utils/helper-utils';
import DateService from '@services/DateService';
import FormattingService from '@services/FormattingService';

const PAGE_CONTEXT = 'Treaties';
const FILTER_SESSION_KEY = 'treatiesFilter';

const TreatiesListingPage = () => {
  const [isTreatyDrawerOpen, setIsTreatyDrawerOpen] = useState(false);
  const [filterSections, setFilterSections] =
    useState<IListingFilterWidgetSection[]>();
  const initialfilterValues =
    Object.keys(getFilter(FILTER_SESSION_KEY)).length > 0
      ? getFilter(FILTER_SESSION_KEY)
      : {
          treatyType: [],
          leaderReinsurer: [],
          currency: [],
          effectiveDate: null,
          status: null,
        };
  const [filterValues, setFilterValues] = useState<IFilterModel>({
    namedFilters: initialfilterValues,
    pagination: {
      pageSize: 10,
      pageNumber: 1,
    },
  });
  const [tableData, setTableData] = useState<
    IListingData & {
      lovs: Record<string, Record<string, string>>;
    }
  >({
    pagedItems: {},
    pageSize: 10,
    pageNumber: 1,
    totalCount: 0,
    lovs: {},
  });

  const [getTreatiesLazy, { loading: treatiesLoading }] =
    useLazyQuery(getTreaties);

  function handlePageChange(
    page: number,
    filterModel = filterValues
  ): Promise<void> {
    return new Promise<void>(() =>
      getListingData({
        ...filterModel,
        pagination: {
          ...filterModel.pagination,
          pageNumber: page,
        },
      })
    );
  }

  const onFilterUpdate = async (filters: IAbstractRecord) => {
    const newFilters = {
      ...filterValues,
      namedFilters: filters,
      pagination: {
        ...filterValues.pagination,
        pageNumber: 1,
      },
    };
    setFilter(filters, FILTER_SESSION_KEY);
    setFilterValues(newFilters);
    getListingData(newFilters);
  };

  const getVariables = (filter = filterValues) => {
    const formattedDateRange = DateService.formatDateRangeBackend(
      filter.namedFilters.effectiveDate
    );
    return {
      SelectedCompany: '41',
      TreatyType: valueOrNull(
        filter.namedFilters.treatyType?.map((type: string) =>
          FormattingService.formatEnumToBackend(type)
        )
      ),
      LeaderReinsurer: isEmpty(filter.namedFilters?.leaderReinsurer)
        ? null
        : [filter.namedFilters.leaderReinsurer],
      Currency: valueOrNull(filter.namedFilters.currency),
      EffectiveFromDate: formattedDateRange?.[0],
      EffectiveToDate: formattedDateRange?.[1],
      Status: valueOrNull(
        filter.namedFilters.status?.map((value: string) =>
          FormattingService.formatEnumToBackend(value)
        )
      ),
      KeywordSearch: filter.searchKey,
      OrderByField: filter.orderBy,
      DescendingField: filter.descending,
      pagination: {
        pageSize: filter.pagination.pageSize,
        pageNumber: filter.pagination.pageNumber,
      },
    };
  };

  const getListingData = (filter = filterValues) => {
    if (!filter) {
      return {};
    }
    const variables = getVariables(filter);
    return getTreatiesLazy({
      variables: variables,
    })
      .then(({ data }) => {
        if (data) {
          const lovs = toLookupsData(data);

          if (!Object.keys(lovs.treatyType).length) {
            lovs.treatyType = filter.namedFilters.treatyType;
          }

          if (!Object.keys(lovs.leaderReinsurer).length) {
            lovs.leaderReinsurer = filter.namedFilters.leaderReinsurer;
          }

          if (!Object.keys(lovs.status).length) {
            lovs.status = filter.namedFilters.status;
          }

          const newFilterSections = filterSectionsContent(
            lovs,
            filter.namedFilters
          );
          setFilterSections(newFilterSections);
          setFilterValues(filter);

          const tableData = mapToListingData(
            data?.Production?.queries?.TreatiesList,
            lovs.treatyType
          );

          setTableData({
            ...tableData,
            pageNumber: tableData.pageNumber,
            pageSize: tableData.pageSize,
            totalCount: tableData.totalCount,
            lovs,
          });
        }
      })
      .catch(() => {
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
      });
  };

  const actions: IEnhancedMenuItem[] = [
    {
      name: 'newTreaty',
      title: '+ New',
      isPrimary: false,
      onClick: () => {
        setIsTreatyDrawerOpen(true);
      },
    },
  ];

  const renderFilter = () => {
    return (
      filterSections && (
        <ListingFilterWidget
          name={''}
          filters={filterSections}
          onApplyFilter={(v) => {
            onFilterUpdate(v);
          }}
        />
      )
    );
  };

  const renderMainChildren = () => {
    return (
      <>
        <ListingWidget
          name="treaties"
          title="Treaties"
          orderByAscendingByDefault
          loading={treatiesLoading}
          data={tableData}
          tableSettings={{
            headers: headers,
          }}
          actions={actions}
          pageContext={PAGE_CONTEXT}
          onPageChange={(filterModel) => {
            const newFilterModel = {
              ...filterValues,
              ...filterModel,
            };
            const page = filterModel.pagination.pageNumber;
            return handlePageChange(page, newFilterModel);
          }}
          disableSelection
          usePagination={true}
        />
        {isTreatyDrawerOpen && (
          <NewTreatyDrawer
            isOpen={isTreatyDrawerOpen}
            onClose={() => {
              setIsTreatyDrawerOpen(false);
            }}
            lovs={tableData.lovs}
          />
        )}
      </>
    );
  };

  return (
    <StaticLayout
      name="Treaties"
      leftChildren={renderFilter()}
      mainChildren={renderMainChildren()}
    />
  );
};

export default TreatiesListingPage;
